import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, Clock, Eye } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import api, { formatApiError } from "../lib/api";
import WarehouseTabs from "../components/WarehouseTabs";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  DataValue,
  PassiveBadge,
  PrimarySecondaryValue,
  formatPresentationTimestamp,
} from "../components/ui/data-presentation";
import {
  ListPagination,
  ListTable,
  ListTableShell,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  OrdinaryPageBody,
  OrdinaryPageHeader,
} from "../components/ui/page-structure";
import { RefreshButton } from "../components/ui/refresh-button";
import { DateRangeSelector } from "../components/ui/search-filter";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SECTIONS = ["hold", "usage", "request", "transfer"];
const HOLD_GEOMETRY = [
  { key: "date", kind: "fixed", width: 150 },
  { key: "age", kind: "fixed", width: 84 },
  { key: "model", kind: "fixed", width: 170 },
  { key: "part", kind: "fill", minWidth: 280 },
  { key: "quantity", kind: "fixed", width: 80 },
  { key: "by", kind: "fixed", width: 190 },
  { key: "notes", kind: "fixed", width: 220 },
];
const USAGE_GEOMETRY = [
  { key: "date", kind: "fixed", width: 150 },
  { key: "ticket-number", kind: "fixed", width: 190 },
  { key: "status", kind: "fixed", width: 120 },
  { key: "source", kind: "fixed", width: 120 },
  { key: "model", kind: "fixed", width: 170 },
  { key: "part", kind: "fixed", width: 280 },
  { key: "quantity", kind: "fixed", width: 80 },
  { key: "serial-number", kind: "fixed", width: 140 },
  { key: "customer", kind: "fixed", width: 220 },
  { key: "notes", kind: "fill", minWidth: 240 },
  { key: "by", kind: "fixed", width: 200 },
];
function number(value, fallback = 1) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}
function pageSize(value) {
  const parsed = number(value, 10);
  return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : 10;
}
function daysSince(value) {
  return Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
}
function textValue(value) {
  return <DataValue value={value} containment="truncate" />;
}
function NotesCell({ id, value, openId, onToggle, rounded = "rounded-sm" }) {
  if (!value) return "—";
  const isLong = value.length > 25;
  return (
    <div className="relative" style={{ display: "inline-block" }}>
      {isLong ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggle(openId === id ? null : id);
          }}
          className="text-left text-zinc-500 hover:text-zinc-700 underline decoration-dotted underline-offset-2"
          data-action-category="utility"
        >
          {value.slice(0, 25)}…
        </button>
      ) : (
        value
      )}
      {openId === id ? (
        <div
          className={`absolute z-20 left-0 bottom-full mb-1 bg-white border border-zinc-200 ${rounded} shadow-brutal p-3 text-xs text-zinc-700 whitespace-pre-wrap`}
          style={{ minWidth: "200px", maxWidth: "280px" }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {value}
        </div>
      ) : null}
    </div>
  );
}

function Section({
  title,
  children,
  result,
  loading,
  error,
  page,
  size,
  onPage,
  onSize,
}) {
  const pagination = result?.pagination || { total: 0, total_pages: 0 };
  return (
    <section
      className="border-b border-zinc-200 last:border-b-0"
      data-testid={`spare-summary-${title.toLowerCase().replaceAll(" ", "-")}`}
    >
      <div className="px-6 py-4">
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      </div>
      {error ? (
        <div className="border-b border-destructive/30 px-6 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {children(result?.data || [], loading, error)}
      <ListPagination
        page={pagination.page || page}
        pageSize={pagination.page_size || size}
        total={pagination.total || 0}
        totalPages={pagination.total_pages || 0}
        loading={loading}
        onPrevious={() => onPage(Math.max(1, page - 1))}
        onNext={() => onPage(page + 1)}
        onPageSizeChange={onSize}
        pageSizeAriaLabel={`${title} rows per page`}
      />
    </section>
  );
}

export default function SparePartSummary() {
  const { warehouses } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [notesPopover, setNotesPopover] = useState(null);
  const branchCode = searchParams.get("branch") || "MAIN";
  const branchWarehouse =
    warehouses.find((warehouse) => warehouse.code === branchCode) ||
    warehouses.find((warehouse) => warehouse.code === "MAIN");
  const dateRange = useMemo(
    () => ({
      from: searchParams.get("date_from") || "",
      to: searchParams.get("date_to") || "",
    }),
    [searchParams],
  );
  const [dateRangeDraft, setDateRangeDraft] = useState(dateRange);
  const sectionState = useCallback(
    (key) => ({
      page: number(searchParams.get(`${key}_page`)),
      size: pageSize(searchParams.get(`${key}_page_size`)),
    }),
    [searchParams],
  );
  const update = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) =>
      value == null || value === ""
        ? next.delete(key)
        : next.set(key, String(value)),
    );
    setSearchParams(next);
  };
  const resetPages = (changes) =>
    update({
      ...changes,
      ...Object.fromEntries(SECTIONS.map((key) => [`${key}_page`, 1])),
    });

  useEffect(() => setDateRangeDraft(dateRange), [dateRange]);
  useEffect(() => {
    api
      .get("/items")
      .then((response) => setItems(response.data))
      .catch(() => setItems([]));
  }, []);
  useEffect(() => {
    if (!branchWarehouse?.id) return;
    let cancelled = false;
    setLoading(true);
    setErrors({});
    const common = {
      paged: true,
      warehouse_id: branchWarehouse.id,
      date_from: dateRange.from || undefined,
      date_to: dateRange.to || undefined,
    };
    const requests = [
      api.get("/holds", {
        params: {
          ...common,
          status: "active",
          page: sectionState("hold").page,
          page_size: sectionState("hold").size,
        },
      }),
      api.get("/usage", {
        params: {
          ...common,
          statuses: ["active", "legacy_active"],
          page: sectionState("usage").page,
          page_size: sectionState("usage").size,
        },
        paramsSerializer: { indexes: null },
      }),
      api.get("/transfer-requests", {
        params: {
          paged: true,
          destination_warehouse_id: branchWarehouse.id,
          awaiting_receipt: true,
          date_from: dateRange.from || undefined,
          date_to: dateRange.to || undefined,
          page: sectionState("request").page,
          page_size: sectionState("request").size,
        },
      }),
      api.get("/transfers", {
        params: {
          ...common,
          status: "completed",
          page: sectionState("transfer").page,
          page_size: sectionState("transfer").size,
        },
      }),
    ];
    Promise.allSettled(requests).then((responses) => {
      if (cancelled) return;
      const next = {};
      const failures = {};
      SECTIONS.forEach((key, index) => {
        if (responses[index].status === "fulfilled")
          next[key] = responses[index].value.data;
        else failures[key] = formatApiError(responses[index].reason);
      });
      setResults(next);
      setErrors(failures);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    branchWarehouse?.id,
    dateRange.from,
    dateRange.to,
    sectionState,
    refreshKey,
  ]);
  const itemById = useMemo(
    () => Object.fromEntries(items.map((item) => [item.id, item])),
    [items],
  );
  const changeBranch = (warehouseId) => {
    const warehouse = warehouses.find((entry) => entry.id === warehouseId);
    if (warehouse) resetPages({ branch: warehouse.code });
  };
  const applyDate = ({ from, to }) => {
    setDateRangeDraft({ from, to });
    resetPages({ date_from: from, date_to: to });
  };
  const controls = (key) => ({
    ...sectionState(key),
    onPage: (page) => update({ [`${key}_page`]: page }),
    onSize: (size) =>
      update({ [`${key}_page_size`]: size, [`${key}_page`]: 1 }),
  });
  const statusTone = { active: "success", legacy_active: "warning" };

  return (
    <OrdinaryPageBody data-testid="spare-part-summary-page">
      <OrdinaryPageHeader
        title="Spare Part Summary"
        actions={
          <>
            <RefreshButton
              onClick={() => setRefreshKey((value) => value + 1)}
              loading={loading}
              accessibleLabel="Refresh Spare Part Summary"
            />
            <DateRangeSelector
              value={dateRangeDraft}
              onChange={setDateRangeDraft}
              onApply={applyDate}
              mode="immediate"
              label={null}
              presentation="header-action"
              aria-label="Spare Part Summary Date Range"
            />
          </>
        }
      />
      <ListTableShell
        className="min-w-0 overflow-x-hidden"
        data-testid="spare-part-summary-panel"
      >
        <WarehouseTabs
          warehouses={warehouses.filter((warehouse) =>
            ["MAIN", "SBY", "DPS"].includes(warehouse.code),
          )}
          active={branchWarehouse?.id}
          onChange={changeBranch}
          testidPrefix="spare-summary-branch"
        />
        <Section
          title="Hold"
          result={results.hold}
          loading={loading}
          error={errors.hold}
          {...controls("hold")}
        >
          {(rows) => (
            <ListTableViewport>
              <ListTable geometry={HOLD_GEOMETRY}>
                <thead>
                  <tr>
                    {["Date", "Age", "Model", "Part", "Qty", "By", "Notes"].map(
                      (label) => (
                        <th key={label}>{label}</th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {!rows.length ? (
                    <ListTableState colSpan={7}>
                      {loading ? "Loading holds…" : "No active holds match."}
                    </ListTableState>
                  ) : (
                    rows.map((hold) => {
                      const item = itemById[hold.item_id];
                      const age = daysSince(hold.created_at);
                      return (
                        <tr key={hold.id}>
                          <td className="text-xs tabular-nums">
                            {formatPresentationTimestamp(hold.created_at)}
                          </td>
                          <td>
                            <PassiveBadge tone={age > 5 ? "danger" : "neutral"}>
                              <Clock className="h-2.5 w-2.5" />
                              {age}d
                            </PassiveBadge>
                          </td>
                          <td className="text-xs">
                            <DataValue value={item?.model} />
                            {(item?.compatible_models || []).filter(
                              (model) => model !== item?.model,
                            ).length ? (
                              <div className="text-[10px] text-zinc-400">
                                {item.compatible_models
                                  .filter((model) => model !== item.model)
                                  .join(", ")}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <PrimarySecondaryValue
                              primary={hold.part_name}
                              secondary={hold.part_number}
                              primaryClassName="text-xs"
                              secondaryClassName="font-semibold tracking-[0.02em]"
                            />
                          </td>
                          <td className="num">{hold.quantity}</td>
                          <td className="max-w-[180px] text-xs">
                            {textValue(hold.created_by_email)}
                          </td>
                          <td
                            className="text-xs text-zinc-500"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <NotesCell
                              id={`hold-${hold.id}`}
                              value={hold.notes}
                              openId={notesPopover}
                              onToggle={setNotesPopover}
                              rounded="rounded-lg"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </ListTable>
            </ListTableViewport>
          )}
        </Section>
        <Section
          title="Usage"
          result={results.usage}
          loading={loading}
          error={errors.usage}
          {...controls("usage")}
        >
          {(rows) => (
            <ListTableViewport>
              <ListTable geometry={USAGE_GEOMETRY}>
                <thead>
                  <tr>
                    {[
                      "Date",
                      "Ticket Number",
                      "Status",
                      "Source",
                      "Model",
                      "Part",
                      "Qty",
                      "SN",
                      "Customer",
                      "Notes",
                      "By",
                    ].map((label) => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!rows.length ? (
                    <ListTableState colSpan={11}>
                      {loading ? "Loading usage…" : "No usage records match."}
                    </ListTableState>
                  ) : (
                    rows.map((usage) => {
                      const item = itemById[usage.item_id];
                      return (
                        <tr key={usage.id}>
                          <td className="text-xs tabular-nums">
                            {formatPresentationTimestamp(usage.created_at)}
                          </td>
                          <td className="max-w-[190px] text-xs">
                            {textValue(usage.service_ticket_number)}
                          </td>
                          <td>
                            <PassiveBadge
                              tone={statusTone[usage.usage_status] || "neutral"}
                            >
                              {usage.usage_status === "legacy_active"
                                ? "Legacy Active"
                                : "Active"}
                            </PassiveBadge>
                          </td>
                          <td>
                            <PassiveBadge
                              tone={
                                usage.source_type === "cannibal"
                                  ? "warning"
                                  : "info"
                              }
                            >
                              {usage.source_type === "cannibal"
                                ? "Cannibal"
                                : "Inventory"}
                            </PassiveBadge>
                            {usage.source_type === "cannibal" ? (
                              <div className="mt-1 break-words text-[10px] text-muted-foreground">
                                Donor {usage.cannibal_device_serial_number || "—"}
                                <br />
                                Recipient {usage.service_ticket_number || "—"} / {usage.recipient_serial_number || "—"}
                              </div>
                            ) : null}
                          </td>
                          <td className="text-xs">
                            <DataValue value={item?.model} />
                            {(item?.compatible_models || []).filter(
                              (model) => model !== item?.model,
                            ).length ? (
                              <div className="text-[10px] text-zinc-400">
                                {item.compatible_models
                                  .filter((model) => model !== item.model)
                                  .join(", ")}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <PrimarySecondaryValue
                              primary={usage.part_name}
                              secondary={usage.part_number}
                              primaryClassName="text-xs"
                              secondaryClassName="font-semibold tracking-[0.02em]"
                            />
                          </td>
                          <td className="num">{usage.quantity}</td>
                          <td className="max-w-[140px] text-xs">
                            {textValue(usage.serial_number)}
                          </td>
                          <td className="max-w-[220px] text-xs">
                            {textValue(usage.customer_name)}
                          </td>
                          <td
                            className="text-xs text-zinc-500"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <NotesCell
                              id={`usage-${usage.id}`}
                              value={usage.notes}
                              openId={notesPopover}
                              onToggle={setNotesPopover}
                            />
                          </td>
                          <td className="max-w-[200px] text-xs">
                            {textValue(usage.created_by_email)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </ListTable>
            </ListTableViewport>
          )}
        </Section>
        <Section
          title="Request Part"
          result={results.request}
          loading={loading}
          error={errors.request}
          {...controls("request")}
        >
          {(rows) => (
            <ListTableViewport>
              <ListTable>
                <thead>
                  <tr>
                    <th style={{ width: "200px" }}>Submitted</th>
                    <th style={{ width: "180px" }}>Staff</th>
                    <th style={{ width: "220px" }}>To</th>
                    <th style={{ width: "70px" }} className="num whitespace-nowrap">Items</th>
                    <th style={{ width: "80px" }} className="num whitespace-nowrap">Qty</th>
                    <th style={{ width: "120px" }}>Status</th>
                    <th>Notes</th>
                    <th style={{ textAlign: "right", width: "130px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!rows.length ? (
                    <ListTableState colSpan={8}>
                      {loading ? "Loading requests…" : "No requests match."}
                    </ListTableState>
                  ) : (
                    rows.map((request) => (
                      <tr key={request.id}>
                        <td className="text-xs tabular-nums">
                          {formatPresentationTimestamp(
                            request.submitted_at || request.created_at,
                          )}
                        </td>
                        <td className="max-w-[180px] text-xs">
                          {textValue(request.created_by_email)}
                        </td>
                        <td className="text-xs font-semibold">
                          {warehouses.find(
                            (warehouse) =>
                              warehouse.id === request.destination_warehouse_id,
                          )?.name || request.destination_warehouse_id}
                        </td>
                        <td className="num">{request.lines.length}</td>
                        <td className="num">
                          {(request.status === "approved" &&
                          request.receipt_required
                            ? request.approved_lines || []
                            : request.lines
                          ).reduce(
                            (total, line) => total + Number(line.quantity || 0),
                            0,
                          )}
                        </td>
                        <td>
                          <PassiveBadge
                            tone={
                              request.status === "approved" ? "info" : "warning"
                            }
                          >
                            {request.status.toUpperCase()}
                          </PassiveBadge>
                        </td>
                        <td className="max-w-[240px] text-xs">
                          <DataValue value={request.notes} />
                        </td>
                        <td>
                          <Button
                            variant="info"
                            size="row-icon"
                            title="View request"
                            aria-label="View request"
                            onClick={() => navigate(`/transfer?view_request=${encodeURIComponent(request.id)}`)}
                          >
                            <Eye />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </ListTable>
            </ListTableViewport>
          )}
        </Section>
        <Section
          title="Recent Transfer"
          result={results.transfer}
          loading={loading}
          error={errors.transfer}
          {...controls("transfer")}
        >
          {(rows) => (
            <ListTableViewport>
              <ListTable>
                <thead>
                  <tr>
                    <th style={{ width: "220px" }}>Date</th>
                    <th>Route</th>
                    <th style={{ width: "70px" }} className="num whitespace-nowrap">Items</th>
                    <th style={{ width: "90px" }} className="num whitespace-nowrap">Total Qty</th>
                    <th style={{ textAlign: "right", width: "150px" }}>Status</th>
                    <th style={{ width: "170px" }}>By</th>
                    <th style={{ width: "150px", textAlign: "right" }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {!rows.length ? (
                    <ListTableState colSpan={7}>
                      {loading ? "Loading transfers…" : "No transfers match."}
                    </ListTableState>
                  ) : (
                    rows.map((transfer) => (
                      <tr key={transfer.id}>
                        <td className="text-xs tabular-nums">
                          {formatPresentationTimestamp(transfer.created_at)}
                        </td>
                        <td>
                          <div className="flex gap-2 text-xs">
                            <span className="font-semibold">
                              {warehouses.find(
                                (warehouse) =>
                                  warehouse.id === transfer.source_warehouse_id,
                              )?.name || transfer.source_warehouse_id}
                            </span>
                            <ArrowRight className="h-3 w-3 text-zinc-400" />
                            <span className="font-semibold">
                              {warehouses.find(
                                (warehouse) =>
                                  warehouse.id ===
                                  transfer.destination_warehouse_id,
                              )?.name || transfer.destination_warehouse_id}
                            </span>
                          </div>
                        </td>
                        <td className="num">{transfer.lines.length}</td>
                        <td className="num">
                          {transfer.lines.reduce(
                            (total, line) => total + Number(line.quantity || 0),
                            0,
                          )}
                        </td>
                        <td>
                          <PassiveBadge
                            tone={transfer.request_id ? "success" : "info"}
                          >
                            {transfer.request_id ? "RECEIVED" : "DIRECT"}
                          </PassiveBadge>
                        </td>
                        <td className="text-xs">{transfer.created_by_email}</td>
                        <td>
                          <Button
                            variant="info"
                            size="row-icon"
                            title="View details"
                            aria-label="View transfer details"
                            onClick={() => navigate(`/transfer?view_transfer=${encodeURIComponent(transfer.id)}`)}
                          >
                            <Eye />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </ListTable>
            </ListTableViewport>
          )}
        </Section>
      </ListTableShell>
    </OrdinaryPageBody>
  );
}
